import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { Alert, Snackbar } from "@mui/material";
import { AddButton } from "@/components/buttons/add-button";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
import type {
	ColDef,
	GetContextMenuItemsParams,
	ICellRendererParams,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ConfigurationPage } from "../components/ConfigurationPage";
import {
	ConfigurationPanel,
	panelColumn,
	rowMenuColumn,
} from "../components/ConfigurationPanel";
import { ConfigurationSearch } from "../components/ConfigurationSearch";
import { CraftLetterBadge } from "./components/CraftLetterBadge";
import { UpsertDeleteCraft } from "./components/UpsertDeleteCraft";
import { useCrafts } from "./hooks/useCraftsQueries";
import type { CraftRow, ModalMode } from "./models/Craft";

const gridStyle = {
	height: "calc(100vh - 360px)",
	minHeight: 320,
	width: "100%",
};

export const CraftsPage = () => {
	const [selected, setSelected] = useState<CraftRow | null>(null);
	const [modal, setModal] = useState<ModalMode>("");
	const [notification, setNotification] = useState<string>("");
	const [error, setError] = useState<string>("");
	const [search, setSearch] = useState("");

	const { crafts, isLoading, error: listError } = useCrafts();

	useEffect(() => {
		if (listError) setError(listError.message);
	}, [listError]);

	const openModal = useCallback((mode: ModalMode, row: CraftRow | null) => {
		setSelected(row);
		setModal(mode);
	}, []);

	const columns = useMemo<ColDef<CraftRow>[]>(
		() => [
			{ field: "CraftName", headerName: "Name", flex: 2, ...panelColumn.text },
			{
				headerName: "Badge",
				width: 90,
				sortable: false,
				cellRenderer: (p: ICellRendererParams<CraftRow>) => (
					<CraftLetterBadge
						name={p.data?.CraftName}
						code={p.data?.Code}
						color={p.data?.Color}
					/>
				),
			},
			{
				field: "WorkOrderCount",
				headerName: "Work Orders",
				width: 130,
				...panelColumn.number,
			},
			{
				field: "TemplateCount",
				headerName: "Templates",
				width: 120,
				...panelColumn.number,
			},
			rowMenuColumn,
		],
		[],
	);

	const getContextMenuItems = useCallback(
		(e: GetContextMenuItemsParams<CraftRow>) => {
			const row = e?.node?.data;
			const result: MenuItemDef[] = [
				{
					name: "New Craft",
					action: () => openModal("new", null),
					icon: '<i class="fas fa-plus"></i>',
				},
			];
			if (row) {
				result.push(
					{
						name: "Edit Craft",
						action: () => openModal("edit", row),
						icon: '<i class="fas fa-edit"></i>',
					},
					{
						name: "Delete Craft",
						action: () => openModal("delete", row),
						icon: '<i class="fas fa-trash"></i>',
					},
				);
			}
			return result;
		},
		[openModal],
	);

	const closeModal = (savedMessage?: string) => {
		setModal("");
		if (savedMessage) setNotification(savedMessage);
	};

	return (
		<>
			<HelmetDexteel title="Crafts - Configuration" />
			<ConfigurationPage
				title="Crafts"
				description="The crafts a work order or template can belong to. A craft in use cannot be deleted."
				filters={
					<ConfigurationSearch
						value={search}
						onChange={setSearch}
						placeholder="Craft name"
					/>
				}
			>
				<ConfigurationPanel
					actions={<AddButton onClick={() => openModal("new", null)} />}
				>
					<div style={gridStyle}>
						<AgGridReact<CraftRow>
							rowData={crafts}
							columnDefs={columns}
							defaultColDef={globalDefaultColDef}
							getRowId={(p) => String(p.data.CraftId)}
							rowSelection="single"
							suppressCellFocus={true}
							quickFilterText={search}
							loading={isLoading}
							getContextMenuItems={getContextMenuItems}
							onRowDoubleClicked={(e) => openModal("edit", e.data ?? null)}
							overlayNoRowsTemplate="No crafts yet"
						/>
					</div>
				</ConfigurationPanel>
			</ConfigurationPage>

			<UpsertDeleteCraft
				modal={modal}
				craft={modal === "new" ? null : selected}
				onHide={closeModal}
			/>

			<Snackbar
				open={!!notification}
				autoHideDuration={2500}
				onClose={() => setNotification("")}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="success" onClose={() => setNotification("")}>
					{notification}
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
